import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Chip
} from '@mui/material'
import {
  Restaurant,
  ArrowBack,
  Assignment,
  CheckCircle
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../utils/axios'
import { differenceInDays, parseISO, addYears } from 'date-fns'

const ProdutorEditais = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [editais, setEditais] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const isCAFActive = () => {
    if (!user?.data_inscricao_caf) return false
    const inscricaoDate = parseISO(user.data_inscricao_caf)
    const expirationDate = addYears(inscricaoDate, 3)
    const today = new Date()
    return today <= expirationDate
  }

  useEffect(() => {
    fetchEditais()
  }, [])

  const fetchEditais = async () => {
    try {
      setLoading(true)
      const response = await api.get('/editais/list.php')
      console.log(response.data.editais);
      if (response.data.success) {
        const editaisWithConcorrentes = await Promise.all(
          (response.data.editais || []).map(async (edital) => {
            try {
              const concorrentesResponse = await api.get(
                `/editais/concorrentes.php?edital_id=${edital.id}`
              )
              console.log('teste');
              return {
                ...edital,
                concorrentes: concorrentesResponse.data.concorrentes || []
              }
            } catch (err) {
              return { ...edital, concorrentes: [] }
            }
          })
        )
        console.log(editaisWithConcorrentes);
        setEditais(editaisWithConcorrentes)
      } else {
        setError(response.data.message || 'Erro ao carregar editais')
      }
    } catch (err) {
      setError('Erro ao carregar editais. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleConcorrer = async (editalId) => {
    if (!isCAFActive()) {
      setError('Seu CAF está expirado. Atualize sua data de inscrição no perfil.')
      return
    }

    try {
      const response = await api.post('/editais/concorrer.php', {
        edital_id: editalId,
        produtor_id: user.id
      })

      if (response.data.success) {
        setSuccess('Inscrição realizada com sucesso!')
        fetchEditais()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(response.data.message || 'Erro ao se inscrever no edital')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao se inscrever no edital')
    }
  }

  const isInscrito = (edital) => {
    return edital.concorrentes?.some(c => c.produtor_id === user?.id)
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/produtor')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Restaurant sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Editais Disponíveis
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {!isCAFActive() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Seu CAF está expirado. Você não pode se inscrever em editais até atualizar
            sua data de inscrição no perfil.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : editais.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nenhum edital disponível no momento
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editais.map((edital) => {
              const inscrito = isInscrito(edital)
              const hoje = new Date()
              const dataFim = new Date(edital.data_fim)
              const emVigor = hoje <= dataFim

              if (!emVigor) return null

              return (
                <Card key={edital.id} elevation={2}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">{edital.titulo}</Typography>
                      <Chip
                        label={inscrito ? 'Inscrito' : 'Disponível'}
                        color={inscrito ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      {edital.descricao}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Início:</strong>{' '}
                        {new Date(edital.data_inicio).toLocaleDateString('pt-BR')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Fim:</strong>{' '}
                        {new Date(edital.data_fim).toLocaleDateString('pt-BR')}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {edital.concorrentes?.length || 0} concorrente(s)
                      </Typography>

                      {inscrito ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Você está inscrito"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Button
                          variant="contained"
                          startIcon={<Assignment />}
                          onClick={() => handleConcorrer(edital.id)}
                          disabled={!isCAFActive()}
                        >
                          Concorrer
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default ProdutorEditais

